<template>
  <div class="download-toast-stack" v-if="pendingExports.length">
    <div
      v-for="item in pendingExports"
      :key="item.key"
      class="download-toast"
      :class="'download-toast--' + item.status"
    >
      <div class="download-toast__icon">
        <i v-if="item.status === 'loading'" class="bi bi-arrow-repeat download-toast__spin"></i>
        <i v-else-if="item.status === 'done'" class="bi bi-check-circle-fill"></i>
        <i v-else class="bi bi-exclamation-triangle-fill"></i>
      </div>

      <div class="download-toast__body">
        <div class="download-toast__label">{{ item.label }}</div>
        <div v-if="item.status === 'loading'" class="download-toast__bar">
          <div class="download-toast__bar-fill"></div>
        </div>
        <div v-else-if="item.status === 'error'" class="download-toast__message">
          {{ item.message || 'No se pudo generar el archivo.' }}
        </div>
      </div>

      <div class="download-toast__actions">
        <button
          v-if="item.status === 'done'"
          type="button"
          class="download-toast__btn"
          :disabled="!!downloadingKeys[item.key]"
          @click="download(item)"
        >
          <i :class="downloadingKeys[item.key] ? 'bi bi-arrow-repeat download-toast__spin' : 'bi bi-download'"></i>
          Descargar
        </button>
        <button type="button" class="download-toast__close" title="Cerrar" @click="dismiss(item)">
          <i class="bi bi-x"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import myaxios from 'axios';

const AUTO_DISMISS_MS = 8000;

export default {
  name: 'DownloadToast',
  data() {
    return {
      downloadingKeys: {},
      autoDismissTimers: {},
    };
  },
  computed: {
    ...mapState('downloads', {
      pendingExports: (state) => state.pendingExports,
    }),
  },
  watch: {
    pendingExports: {
      deep: true,
      handler(items) {
        items.forEach((item) => {
          if (item.status === 'done' && !this.autoDismissTimers[item.key]) {
            this.autoDismissTimers[item.key] = setTimeout(() => {
              this.dismiss(item);
            }, AUTO_DISMISS_MS);
          }
        });
      },
    },
  },
  beforeDestroy() {
    Object.values(this.autoDismissTimers).forEach((timer) => clearTimeout(timer));
  },
  methods: {
    dismiss(item) {
      if (this.autoDismissTimers[item.key]) {
        clearTimeout(this.autoDismissTimers[item.key]);
        delete this.autoDismissTimers[item.key];
      }
      this.$store.commit('downloads/removePendingExport', { key: item.key });
    },
    isPDF(url) {
      return (url || '').toLowerCase().endsWith('.pdf');
    },
    getName(url) {
      if (!url) return '';
      return url.split('/').pop();
    },
    async download(item) {
      const file = item.downloadRow;
      if (!file || this.downloadingKeys[item.key]) return;
      this.$set(this.downloadingKeys, item.key, true);
      try {
        const isPdfFile = this.isPDF(file.url);
        const filename = this.getName(file.url) || (isPdfFile ? `descarga-${file.id}.pdf` : `descarga-${file.id}.csv`);

        const presignedResponse = await this.$http.post(`/api/downloads/getPresigned/${file.id}`);
        const fileURL = presignedResponse.data.data;

        // Instancia limpia: el axios global tiene interceptores que agregan el Authorization
        // Bearer, que rompe la URL presigneada de S3 si llega como header propio del request.
        const uninterceptedAxios = myaxios.create();
        const fileResponse = await uninterceptedAxios.get(fileURL, { responseType: 'blob' });

        const blobType = isPdfFile ? 'application/pdf' : 'text/csv';
        const blob = new Blob([fileResponse.data], { type: blobType });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);

        this.dismiss(item);
      } catch (error) {
        console.error('DownloadToast download:', error);
        alert('No se pudo descargar el archivo. Intentá de nuevo en unos minutos.');
      } finally {
        this.$set(this.downloadingKeys, item.key, false);
      }
    },
  },
};
</script>

<style scoped>
.download-toast-stack {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 320px;
}

.download-toast {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}

.download-toast__icon {
  font-size: 18px;
  line-height: 1;
  padding-top: 2px;
}

.download-toast--done .download-toast__icon {
  color: #198754;
}

.download-toast--error .download-toast__icon {
  color: #dc3545;
}

.download-toast__body {
  flex: 1;
  min-width: 0;
}

.download-toast__label {
  font-size: 13px;
  color: var(--text);
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.download-toast__message {
  font-size: 12px;
  color: #dc3545;
}

.download-toast__bar {
  height: 4px;
  background: var(--border-soft);
  border-radius: 2px;
  overflow: hidden;
}

.download-toast__bar-fill {
  height: 4px;
  width: 40%;
  background: var(--brand-strong);
  border-radius: 2px;
  animation: download-toast-indeterminate 1.2s ease-in-out infinite;
}

@keyframes download-toast-indeterminate {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}

.download-toast__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.download-toast__btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 4px 8px;
  border: 1px solid var(--brand-strong);
  color: var(--brand-strong);
  background: var(--surface);
  border-radius: 4px;
  cursor: pointer;
}

.download-toast__btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.download-toast__close {
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.download-toast__spin {
  display: inline-block;
  animation: download-toast-spin 0.8s linear infinite;
}

@keyframes download-toast-spin {
  to { transform: rotate(360deg); }
}
</style>
